import { Component, OnDestroy, OnInit } from '@angular/core';
import { StudyEditorContext } from '../../study-editor.context';
import { ObservationVariable } from '../../../shared/model/observation-variable.model';
import { DatasetService } from '../../../shared/dataset/service/dataset.service';
import { DatasetVariable } from '../../../shared/study/model/dataset-variable';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';
import { AlertService } from '../../../shared/alert/alert.service';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { TranslateService } from '@ngx-translate/core';
import { StudySettingVariable } from './study-setting-variable.model';
import { DataTypeEnum } from '../../../shared/ontology/data-type.enum';
import { StudyLevelVariableData } from '../../../shared/dataset/model/study-level-variable-data';
import { isNumeric } from '../../../shared/util/is-numeric';
import { FileService } from '../../../shared/file/service/file.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FileManagerComponent } from '../../../file-manager/file-manager.component';
import { ParamContext } from '../../../shared/service/param.context';
import { Principal } from '../../../shared';
import { ADD_STUDY_SETTINGS_VARIABLES_PERMISSIONS, MODIFY_STUDY_SETTINGS_VALUES_PERMISSIONS } from '../../../shared/auth/permissions';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { HELP_MANAGE_STUDIES_SETTINGS } from '../../../app.constants';
import { VariableUtilityService } from '../../shared/services/variable-utility.service';

@Component({
    selector: 'jhi-study-settings',
    templateUrl: './study-settings.component.html',
    styleUrls: ['./study-settings.component.scss']
})
export class StudySettingsComponent implements OnInit, OnDestroy {

    protected readonly ADD_STUDY_SETTINGS_VARIABLES_PERMISSIONS = ADD_STUDY_SETTINGS_VARIABLES_PERMISSIONS;
    protected readonly MODIFY_STUDY_SETTINGS_VALUES_PERMISSIONS = MODIFY_STUDY_SETTINGS_VALUES_PERMISSIONS;

    // List of variables to hide from the variables table
    HIDE_FROM_TABLE = [8373, 8100, 8180, 8070, 8040, 8170, 8256, 8411, 8412, 8413, 8257, 8140, 8150, 8155];
    HIDE_FROM_VARIABLE_SELECTOR = [8180, 8070, 8040, 8170, 8256, 8411, 8412, 8413, 8257, 8140, 8150, 8155];

    readonly VariableTypeEnum = VariableTypeEnum;
    readonly DataTypeEnum = DataTypeEnum;

    private isReloadPending = false; // Tracks if a reload is already pending
    private reloadDelay = 500; // Debouncing delay (milliseconds)
    private reloadTimeout: any; // Timeout reference for debouncing
    isFileStorageConfigured: boolean = false;

    studySettings: StudySettingVariable[];
    filteredStudySettings: StudySettingVariable[];
    selectedCell: { row: number; col: number; variable: StudySettingVariable } | null = null;
    fileCount: number = 0;
    fileVariableIds: string[] = [];
    hoveredRow: number | null = null;

    constructor(public studyEditorContext: StudyEditorContext,
                private datasetService: DatasetService,
                public translateService: TranslateService,
                private alertService: AlertService,
                private fileService: FileService,
                private modalService: NgbModal,
                private paramContext: ParamContext,
                private variableUtilityService: VariableUtilityService,
                public principal: Principal) {
    }

    ngOnInit(): void {
        this.loadStudySetting();
        this.studyEditorContext.currentDataset = this.studyEditorContext.getSettingDataset();
        this.fileService.isFileStorageConfigured().then((isFileStorageConfigured) => this.isFileStorageConfigured = isFileStorageConfigured);
    }

    async loadStudySetting() {
        this.studySettings = await this.datasetService.getStudyLevelSettings(this.studyEditorContext.studyDetails.id).toPromise() as StudySettingVariable[];
        this.filteredStudySettings = this.studySettings.filter(variable => {
            variable.previousValue = variable.value;
            // Only show variables that are not in the hidden list
            return !this.HIDE_FROM_TABLE.includes(variable.termId);
        });
        this.loadFilesData();
    }

    /**
     * Efficiently reload study settings by delaying call to load settings to allow batching.
     */
    scheduleReload(): void {
        if (this.isReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.reloadTimeout);
        } else {
            this.isReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.reloadTimeout = setTimeout(() => {
            this.loadStudySetting();
            this.isReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    async addVariable(variable: VariableDetails) {
        if (!variable) {
            return;
        }

        const request: DatasetVariable = {
            variableId: parseInt(variable.id, 10),
            variableTypeId: VariableTypeEnum.STUDY_DETAIL,
            studyAlias: variable.alias || variable.name
        };

        try {
            await this.datasetService.addStudyLevelSettings(this.studyEditorContext.studyDetails.id, request).toPromise();
            this.studyEditorContext.existingStudyLevelVariableIds.push(request.variableId);
            const pairNameVariableId = this.variableUtilityService.getNamePairByVariableId(request.variableId);
            if (pairNameVariableId !== request.variableId) {
                this.studyEditorContext.existingStudyLevelVariableIds.push(pairNameVariableId);
            }
        } catch (error) {
            this.alertService.error('study-editor.study-settings.error.variable.add', { param: request.studyAlias });
        }

        // Schedule reload instead of immediate reload
        this.scheduleReload();
    }

    async removeVariables(variablesIds: number[]) {
        try {
            await this.datasetService.removeStudyLevelSettings(this.studyEditorContext.studyDetails.id, variablesIds).toPromise();
            this.studyEditorContext.existingStudyLevelVariableIds = this.studyEditorContext.existingStudyLevelVariableIds.filter(id => {
                const pairNameVariableId = this.variableUtilityService.getNamePairByVariableId(id);
                return !variablesIds.includes(id) && id !== pairNameVariableId;
            });

        } catch (error) {
            this.alertService.error('study-editor.study-settings.error.variable.remove');
        }

        // Schedule reload instead of immediate reload
        this.scheduleReload();
    }

    ngOnDestroy(): void {
        // Clear the reload timeout when the component is destroyed
        if (this.reloadTimeout) {
            clearTimeout(this.reloadTimeout);
        }

    }

    selectCell(studySetting: StudySettingVariable, rowIndex: number, colIndex: number) {

        // Deselect the previously selected cell
        if (this.selectedCell) {
            this.selectedCell.variable.isEditing = false;
            this.selectedCell.row = -1;
            this.selectedCell.col = -1;
        }

        this.selectedCell = { row: rowIndex, col: colIndex, variable: studySetting };
        studySetting.isEditing = true;
    }

    variableDataTypeIs(variable: StudySettingVariable, type: DataTypeEnum): boolean {
        if (variable.variableType && variable.variableType.toString() === VariableTypeEnum[VariableTypeEnum.STUDY_DETAIL]) {
            return variable.dataType && variable.dataType.valueOf() === DataTypeEnum[type];
        }
        return false;
    }

    trackId(item: StudySettingVariable): number | undefined {
        return item.termId;
    }

    onCellBlur(row: number, col: number): void {
        this.updateStudyVariable();
        this.resetSelected();
    }

    resetSelected() {
        if (this.selectedCell) {
            this.selectedCell.variable.isEditing = false;
            this.selectedCell = null;
        }
    }

    async updateStudyVariable() {
        if (!this.selectedCell?.variable) {
            return;
        }

        const variable = this.selectedCell.variable;

        // Do not update if value hasn't changed.
        if (variable.previousValue === variable.value) {
            return;
        }

        if (this.validateValue(variable.value)) {
            this.alertService.error('study-editor.study-dataset.error.invalid-value');
            variable.value = variable.previousValue;
            return;
        }

        // Store the previous value before update
        const previousValue = variable.previousValue || variable.value;

        const data: StudyLevelVariableData = {
            studyId: this.studyEditorContext.studyDetails.id,
            variableId: variable.termId,
            value: variable.value,
            // If the variable is categorical, store the value as categoricalValueId
            categoricalValueId: variable.dataTypeCode === 'C' ?
                (isNumeric(variable.value) ? variable.value : null) : null,
            updateOrderToMoveUp: false
        };

        try {
            await this.datasetService.updateStudyLevelSetting(
                this.studyEditorContext.studyDetails.id,
                data
            ).toPromise();

            // Update was successful, store the new value as previous
            variable.previousValue = variable.value;
        } catch (responseError) {
            // Revert both the value and previousValue if update failed
            variable.value = previousValue;
            variable.previousValue = previousValue;
            if (responseError.error) {
                this.alertService.error('study-editor.study-settings.error.update', { param: responseError.error.errors[0].message });
            } else {
                this.alertService.error('error.general');
            }

            this.scheduleReload();

        }
    }

    validateValue(value) {
        const invalidChars = /[<>?|%]/;
        return invalidChars.test(value);
    }

    onCellKeyDown(event: KeyboardEvent, row: number, col: number) {
        switch (event.key) {
        case 'Enter':
        case 'Escape':
            event.preventDefault();
            this.onCellBlur(row, col);
            break;
        case 'Tab':
            event.preventDefault();
            this.onCellBlur(row, col);
            const nextRow = row + (event.shiftKey ? -1 : 1);
            if (nextRow >= 0 && nextRow < this.filteredStudySettings.length) {
                this.selectCell(this.filteredStudySettings[nextRow], nextRow, col);
            }
            break;
        case 'ArrowUp':
            if (row > 0) {
                event.preventDefault();
                this.onCellBlur(row, col);
                this.selectCell(this.filteredStudySettings[row - 1], row - 1, col);
            }
            break;
        case 'ArrowDown':
            if (row < this.filteredStudySettings.length - 1) {
                event.preventDefault();
                this.onCellBlur(row, col);
                this.selectCell(this.filteredStudySettings[row + 1], row + 1, col);
            }
            break;
        }
    }

    loadFilesData() {
        if (this.isFileStorageConfigured) {
            this.fileService.listFileMetadata(null, null, null, null, null,
                this.studyEditorContext.studyDetails.id, null).toPromise().then((response) => {
                const files = response.body;
                const fileVariableIdsTemp: string[] = [];
                if (files && files.length) {
                    this.fileCount = files.length;
                    files.forEach(function(file) {
                        if (file.variables && file.variables.length) {
                            file.variables.forEach(variable => fileVariableIdsTemp.push(variable.id.toString()));
                        }
                    });
                }
                this.fileVariableIds = fileVariableIdsTemp;
            });
        }
    };

    isFileButtonVisible(studySetting: StudySettingVariable): boolean {
        return this.isFileStorageConfigured && this.fileCount > 0 && this.fileVariableIds.length > 0 && this.fileVariableIds.includes(studySetting.termId.toString());
    }

    showFiles(studySetting?: StudySettingVariable) {
        const modalRef = this.modalService.open(FileManagerComponent as Component, { size: 'xl', backdrop: 'static' });
        modalRef.componentInstance.projectId = this.studyEditorContext.studyDetails.id;
        modalRef.componentInstance.datasetId = null;
        modalRef.componentInstance.variableName = studySetting?.name;
        modalRef.result.then((result) => {
            this.scheduleReload();
        }, () => {
            this.scheduleReload();
        });
    }

    setHoveredRow(index: number | null): void {
        this.hoveredRow = index;
    }

    async detachOrDeleteFilesIfAny(variables: ObservationVariable[]) {
        const variableIds = variables.map(variable => variable.termId);

        const doContinue = await this.checkVariableHasMeasurementData(variableIds);
        if (!doContinue) {
            return;
        }

        if (this.isFileStorageConfigured) {
            const fileCountResp = await this.fileService.getFileCountStudy(variableIds, null, null, this.studyEditorContext.studyId).toPromise();
            const fileCount = parseInt(fileCountResp.headers.get('X-Total-Count'), 10);
            if (fileCount > 0) {
                const modalInstance = this.showFileDeletionOptions(fileCount);
                modalInstance.result.then(() => {
                    this.fileService.removeFilesStudy(variableIds, null, this.studyEditorContext.studyId).toPromise()
                        .then(() => {
                            this.removeVariables(variableIds);
                            this.loadFilesData();
                        });
                }, () => {
                    this.fileService.detachFilesStudy(variableIds, null, this.studyEditorContext.studyId).toPromise()
                        .then(() => {
                            this.removeVariables(variableIds);
                            this.loadFilesData();
                        });
                });
            } else {
                this.removeVariables(variableIds);
            }
        } else {
            this.removeVariables(variableIds);
        }
    };

    showFileDeletionOptions(fileCount: number) {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.file-delete-options.message', { param: fileCount });
        confirmModalRef.componentInstance.cancelLabel = this.translateService.instant('study-editor.file-delete-options.leave-them');
        confirmModalRef.componentInstance.confirmLabel = this.translateService.instant('study-editor.file-delete-options.delete-them');
        return confirmModalRef;
    };

    checkVariableHasMeasurementData(variableIds: number[]) {
        if (variableIds.length === 0) {
            return Promise.resolve(false); // No variables to delete, return false to cancel
        }

        let hasMeasurementData = false;
        this.studySettings.forEach((studySetting) => {
            if (variableIds.includes(studySetting.termId) && studySetting.value) {
                hasMeasurementData = true;
            }
        });
        if (hasMeasurementData) {
            // Show confirmation modal and handle user choice
            const modalRef = this.modalService.open(ModalConfirmComponent);
            modalRef.componentInstance.title = this.translateService.instant('study-editor.germplasm-checks.confirmation');
            modalRef.componentInstance.message = this.translateService.instant('study-editor.study-settings.remove.confirmation.message');

            return modalRef.result
                .then(() => true) // User confirms (true)
                .catch(() => false); // User cancels (false)
        } else {
            return Promise.resolve(true); // Variables dont have data, return true to continue
        }
    };

    protected readonly HELP_MANAGE_STUDIES_SETTINGS = HELP_MANAGE_STUDIES_SETTINGS;
}
